import { CommonModule } from '@angular/common';
import { Component, computed, inject, input, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { Timestamp } from '@angular/fire/firestore';
import { FormBuilder, FormGroup, FormsModule, Validators } from '@angular/forms';
import { Dialog } from '@capacitor/dialog';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { map, shareReplay, take } from 'rxjs/operators';
import { AppCommonModule } from 'src/app/common.module';
import { MainDialogComponent } from 'src/app/components/main-dialog/main-dialog.component';
import { ContractorOfferDialogComponent } from 'src/app/components/partner-home/offer/contractor-offer.dialog';
import { SegmentListComponent } from 'src/app/components/segment-list/segment-list.component';
import { SelectDayComponent } from 'src/app/components/select-day/select-day.component';
import { SelectedUser, SelectUserButtonComponent } from 'src/app/components/select-user/select-user.component';
import { ContractorOffer } from 'src/app/models/contractor-offer.model';
import { PartnerHistoryItem, PartnerNote, PartnerNoteStatus } from 'src/app/models/partner-note.model';
import { TimestampPipe } from 'src/app/pipes/timestamp/timestamp.pipe';
import { ContractorOffersService } from 'src/app/services/contractor-offers.service';
import { ScrollService } from 'src/app/services/scroll.service';
import { UsersService } from 'src/app/services/users.service';
import { PartnerSubcontractorDialogComponent } from 'src/app/subcontractors/dialog/subcontractor/partner/subcontractor.dialog';
import { Roles } from 'src/app/utility/role';

@Component({
  selector: 'app-partner-note-dialog',
  templateUrl: './partner-note.dialog.html',
  styleUrls: ['./partner-note.dialog.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AppCommonModule,
    CommonModule,
    FormsModule,
    MainDialogComponent,
    TimestampPipe,
    SegmentListComponent,
    SelectDayComponent,
    SelectUserButtonComponent,
  ],
})
export class PartnerNoteDialogComponent implements OnInit {
  private contractorOffersService = inject(ContractorOffersService);
  private scrollService = inject(ScrollService);
  private translate = inject(TranslateService);
  private usersService = inject(UsersService);

  private readonly changeStatuses = ['prospect', 'contacted', 'booked', 'meeting'];

  item = input<PartnerNote>();

  formGroup: FormGroup;

  readonly contactTypes = ['phone', 'email', 'meeting'];
  readonly statusSegmentButtons = [
    {
      value: 'prospect',
      name: 'live.status.prospect',
      show: true,
    },
    {
      value: 'contacted',
      name: 'live.status.contacted',
      show: true,
    },
    {
      value: 'booked',
      name: 'live.status.booked',
      show: true,
    },
    {
      value: 'meeting',
      name: 'live.status.meeting',
      show: true,
    },
    {
      value: 'offered',
      name: 'live.status.offered',
      show: true,
      disabled: true,
    },
    {
      value: 'contractSend',
      name: 'live.status.contractSend',
      show: true,
      disabled: true,
    },
    {
      value: 'done',
      name: 'live.status.done',
      show: true,
      disabled: true,
    },
    {
      value: 'archived',
      name: 'live.status.archived',
      show: true,
      disabled: true,
    },
  ];

  history = signal<(PartnerHistoryItem & { show: boolean })[]>([]);
  newContact = signal(false);
  roleS = computed(() => this.usersService.role());

  premiumPartners$ = this.usersService.getList(null, { role: Roles.partner }).pipe(
    map((users) => users.filter((it) => it.rights?.premiumPartner)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  constructor(
    private formBuilder: FormBuilder,
    private _modal: ModalController,
    private modalCtrl: ModalController,
  ) { }

  ngOnInit(): void {
    this.formGroup = this.formBuilder.group({
      guid: [this.item()?.guid ?? null],
      contractor: [this.item()?.contractor ?? null],
      inviter: [this.item()?.inviter ?? null],
      target: [this.item()?.target ?? '', Validators.required],
      information: [this.item()?.information ?? ''],
      notes: [this.item()?.notes ?? ''],
      contact: [''],
      contactType: [''],
      interested: [''],
      future: [''],
      date: [this.item()?.date ?? Timestamp.now()],
      status: [this.item()?.status ?? 'prospect'],
      offer: [this.item()?.offer ?? null],
      history: [this.item()?.history ?? []],
      startDate: [this.item()?.startDate ?? null],
      trainingDate: [this.item()?.trainingDate ?? null],
      sendNotification: [this.item()?.sendNotification ?? false],
      notificationDate: [this.item()?.notificationDate ?? Timestamp.now()],
    });
    if (this.item()?.history?.length > 0) {
      this.history.set(this.item().history.map((it) => ({ ...it, show: false })));
    }
  }

  async onOffer(status?: string) {
    const offerGuid = this.formGroup.get('offer').value;
    let item = {
      contractorName: this.formGroup.get('target').value,
      address: { street: '', postalCode: '', city: '' },
      status,
    };
    if (offerGuid) {
      item = await firstValueFrom(this.contractorOffersService.get(offerGuid));
    }
    const itemStatus = this.formGroup.get('status').value;
    this.modalCtrl.create({
      component: ContractorOfferDialogComponent,
      componentProps: { item, status: itemStatus },
      cssClass: ['stack-modal', 'workspace-modal'],
      backdropDismiss: false,
    }).then((m) => {
      m.present();

      m.onDidDismiss<ContractorOffer>().then((data) => {
        if (data.data) {
          this.formGroup.get('offer').setValue(data.data.guid);
          if (status === 'accepted') {
            this.formGroup.get('status').setValue('contractSend');
          } else {
            this.formGroup.get('status').setValue('offered');
          }
          this.save();
        }
      });
    });
  }

  onContract() {
    const guid = this.formGroup.get('offer').value;
    if (guid) {
      this.contractorOffersService.get(guid).pipe(
        take(1),
      ).subscribe((item) => {
        const status = this.formGroup.get('status').value;
        this.modalCtrl.create({
          component: ContractorOfferDialogComponent,
          componentProps: {
            item,
            status,
          },
          cssClass: ['stack-modal', 'workspace-modal'],
          backdropDismiss: false,
        }).then((m) => {
          m.present();

          m.onDidDismiss<ContractorOffer>().then((data) => {
            if (data.data?.status === 'accepted') {
              this.formGroup.get('status').setValue('contractSend');
              this.save();
            }
          });
        });
      });
    } else {
      this.onOffer('accepted');
    }
  }

  onStatus(status: PartnerNoteStatus) {
    if (status === 'deleted') {
      Dialog.confirm({
        message: this.translate.instant('site.archive'),
        okButtonTitle: this.translate.instant('general.yes'),
        cancelButtonTitle: this.translate.instant('general.no'),
      }).then((dialog) => {
        if (dialog.value) {
          this.formGroup.get('status').setValue(status);
          this.save();
        }
      });
    } else {
      this.formGroup.get('status').setValue(status);
      this.save();
    }
  }

  openServices() {
    const item = this.formGroup.value as PartnerNote;
    this.modalCtrl.create({ component: PartnerSubcontractorDialogComponent, componentProps: { item } }).then((m) => {
      m.present();
    });
  }

  onDateChange(date: string, field: string) {
    this.formGroup.get(field).setValue(date);
  }

  statusSegmentChanged(status: string) {
    if (status !== this.formGroup.get('status').value && this.changeStatuses.includes(status)) {
      this.onStatus(status as PartnerNoteStatus);
    }
  }

  onNotificationDate(value: string) {
    const date = new Date(value);
    const timestamp = Timestamp.fromDate(date);
    this.formGroup.get('notificationDate').setValue(timestamp);
  }

  onNew() {
    this.newContact.set(true);
    this.scrollService.scrollToTop();
  }

  changeUser(user: SelectedUser) {
    this.formGroup.get('inviter').setValue(user.guid);
    this.save(true);
  }

  save(skipHistory = false) {
    const item = this.formGroup.value;
    if (item.contactType === 'meeting') {
      item.status = 'booked';
    } else if (['phone', 'email'].includes(item.contactType)) {
      item.status = 'contacted';
    }
    if (!skipHistory) {
      const latestHistory = item.history[item.history.length - 1];
      if (latestHistory == null || latestHistory.status !== item.status) {
        const history: PartnerHistoryItem = {
          status: item.status,
          contact: item.contact,
          contactType: item.contactType,
          interested: item.interested,
          future: item.future,
          date: Timestamp.now(),
          user: { guid: this.usersService.currentUserS().guid, name: this.usersService.currentUserS().displayName },
        };
        item.history.push(history);
      }
      delete item.contact;
      delete item.contactType;
      delete item.interested;
      delete item.future;
    }
    this.dismiss(item);
  }

  dismiss(item?: PartnerNote) {
    this._modal.dismiss(item);
  }
}
